"use client";

import { useEffect, useRef, useState } from "react";
import { useImageStore } from "@/stores/imageStore";
import { showToast } from "@/lib/toast";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ModalUploadImageProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export default function ModalUploadImage({
  isOpen,
  onClose,
  onComplete,
}: ModalUploadImageProps) {
  const { upload, isUploading } = useImageStore();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [title, setTitle] = useState("");
  const [alt, setAlt] = useState("");
  const [caption, setCaption] = useState("");
  const [linkImage, setLinkImage] = useState("");
  const [rel, setRel] = useState<string>("nofollow");
  const [target, setTarget] = useState<string>("_self");
  const dropRef = useRef<HTMLDivElement>(null);
  const [imgWidth, setImgWidth] = useState<number | null>(null);
  const [imgHeight, setImgHeight] = useState<number | null>(null);
  const isValidUrl = (v: string) => {
    try {
      if (!v) return false;
      new URL(v);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setPreview("");
    setTitle("");
    setAlt("");
    setCaption("");
    setLinkImage("");
    setRel("nofollow");
    setTarget("_self");
  }, [isOpen]);

  const onFiles = (f: FileList | null) => {
    const fl = f?.[0];
    if (!fl) return;
    if (!fl.type.startsWith("image/")) {
      showToast.error("Lỗi", "Vui lòng chọn file hình ảnh");
      return;
    }
    setFile(fl);
    const url = URL.createObjectURL(fl);
    setPreview(url);

    // Tự động đặt tiêu đề từ tên file (bỏ extension)
    const nameWithoutExt = fl.name.replace(/\.[^/.]+$/, "");
    setTitle(nameWithoutExt);

    const img = new Image();
    img.onload = () => {
      setImgWidth(img.naturalWidth || (img as any).width || null);
      setImgHeight(img.naturalHeight || (img as any).height || null);
    };
    img.src = url;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    const ok = await upload(file, {
      title,
      alt,
      caption,
      linkImage,
      rel: rel as any,
      target: target as any,
    });
    if (ok) {
      onComplete();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-6xl p-0">
        <form onSubmit={handleSubmit}>
          <DialogHeader className="px-6 py-4 border-b border-gray-200">
            <DialogTitle>Thêm mới hình ảnh</DialogTitle>
          </DialogHeader>

          <div className="p-6">
            {!preview ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <div
                  ref={dropRef}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={handleDrop}
                  className="w-full max-w-xl rounded-xl border-2 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-white py-20 px-8 text-center text-gray-600 hover:border-blue-400 hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className="p-4 bg-blue-50 rounded-full">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-12 h-12 text-blue-500"
                      >
                        <path d="M2.25 6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0119.5 19.5h-15A2.25 2.25 0 012.25 17.25V6.75zM6 9.75a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z" />
                        <path d="M3 16.5l4.94-4.94a1.5 1.5 0 012.12 0L15 16.5l2.47-2.47a1.5 1.5 0 012.12 0L21 15.34V17.25A2.25 2.25 0 0118.75 19.5h-13.5A2.25 2.25 0 013 17.25v-.75z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-gray-700 mb-1">
                        Kéo & thả hoặc bấm để thêm ảnh
                      </div>
                      <div className="text-sm text-gray-500">
                        Hỗ trợ: JPG, PNG, GIF, WEBP (tối đa 10MB)
                      </div>
                    </div>
                    <label className="mt-2 inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-medium cursor-pointer hover:from-blue-700 hover:to-blue-600 transition-all shadow-md hover:shadow-lg">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                        />
                      </svg>
                      Chọn ảnh từ máy
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onFiles(e.target.files)}
                      />
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="relative bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex justify-center items-center">
                      <img
                        src={preview}
                        alt="Preview"
                        className="rounded-lg shadow-md max-h-80 w-auto object-contain"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <label className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white border-2 border-gray-300 text-gray-700 text-sm font-medium cursor-pointer hover:border-blue-500 hover:text-blue-600 transition-all">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                        />
                      </svg>
                      Đổi ảnh
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onFiles(e.target.files)}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setPreview("");
                        setFile(null);
                        setImgWidth(null);
                        setImgHeight(null);
                      }}
                      className="px-4 py-2 rounded-lg bg-red-50 border-2 border-red-200 text-red-600 text-sm font-medium hover:bg-red-100 hover:border-red-300 transition-all"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                        />
                      </svg>
                    </button>
                  </div>
                  {file && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                      <div className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">
                        Thông tin tệp tin
                      </div>
                      <div className="space-y-2 text-sm text-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Tên tệp:</span>
                          <span
                            className="font-medium text-gray-900 truncate ml-2 max-w-[200px]"
                            title={file.name}
                          >
                            {file.name}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Loại:</span>
                          <span className="font-medium text-gray-900">
                            {file.type}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Dung lượng:</span>
                          <span className="font-medium text-gray-900">
                            {Math.round(file.size / 1024)} KB
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Kích thước:</span>
                          <span className="font-medium text-gray-900">
                            {imgWidth ?? "-"} x {imgHeight ?? "-"} px
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Thông tin ảnh
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Tiêu đề
                    </label>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Nhập tiêu đề cho ảnh..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      URL ảnh (tự động)
                    </label>
                    <input
                      value={
                        file
                          ? `https://localhost:3000/uploads/images/${title}${file.name.substring(
                              file.name.lastIndexOf(".")
                            )}`
                          : ""
                      }
                      disabled
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Alt Text (SEO)
                    </label>
                    <input
                      value={alt}
                      onChange={(e) => setAlt(e.target.value)}
                      placeholder="Mô tả thay thế cho ảnh..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Caption
                    </label>
                    <input
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Chú thích cho ảnh..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Link ảnh{" "}
                      <span className="text-gray-500 font-normal">
                        (tùy chọn)
                      </span>
                    </label>
                    <input
                      value={linkImage}
                      onChange={(e) => setLinkImage(e.target.value)}
                      placeholder="https://example.com"
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        linkImage && !isValidUrl(linkImage)
                          ? "border-red-500 focus:ring-red-500 bg-red-50"
                          : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                      }`}
                    />
                    {linkImage && !isValidUrl(linkImage) && (
                      <p className="text-xs text-red-600 mt-1">
                        URL không hợp lệ
                      </p>
                    )}
                  </div>
                  {linkImage.trim() && isValidUrl(linkImage) && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                        Cấu hình liên kết
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                            Rel Attribute
                          </label>
                          <select
                            value={rel}
                            onChange={(e) => setRel(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                          >
                            <option value="nofollow">nofollow</option>
                            <option value="noreferrer">noreferrer</option>
                            <option value="noopener">noopener</option>
                            <option value="sponsored">sponsored</option>
                            <option value="ugc">ugc</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1.5">
                            Thuộc tính SEO cho liên kết
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                            Target
                          </label>
                          <select
                            value={target}
                            onChange={(e) => setTarget(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                          >
                            <option value="_self">_self (tab hiện tại)</option>
                            <option value="_blank">_blank (tab mới)</option>
                            <option value="_parent">_parent</option>
                            <option value="_top">_top</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1.5">
                            Cách mở liên kết
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium cursor-pointer disabled:opacity-50"
            >
              Hủy
            </button>
            {preview && (
              <button
                type="submit"
                disabled={
                  !file ||
                  (linkImage.trim() && !isValidUrl(linkImage)) ||
                  isUploading
                }
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all font-medium shadow-md hover:shadow-lg"
              >
                {isUploading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Đang tải...
                  </span>
                ) : (
                  "Tạo ảnh"
                )}
              </button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
